import { apiFetch } from '@/api/client'
import type { SortDirection } from '@/api/sorting'

/**
 * Where a fair sits relative to today, computed by the backend from the period in the business
 * time zone. Deliberately not derived here: the client's clock and time zone are not the ones the
 * `state` filter uses, and a list that disagrees with its own filter is worse than one extra field.
 */
export const TradeFairState = {
  UPCOMING: 'UPCOMING',
  RUNNING: 'RUNNING',
  PAST: 'PAST',
} as const

export type TradeFairState = (typeof TradeFairState)[keyof typeof TradeFairState]

export type TradeFair = {
  id: string
  name: string
  /** ISO date, `2026-09-10`. No time and no zone — a fair starts on a day, not at an instant. */
  startDate: string
  /** Inclusive: a one-day fair carries the same date at both ends. */
  endDate: string
  city: string
  /** Our own stand, not the visitor's. */
  hall: string | null
  booth: string | null
  notes: string | null
  state: TradeFairState
  /**
   * How many of the fair's leads the caller may see — all of them for an administrator. The same
   * number the lead list totals when filtered by this fair.
   */
  leadCount: number
}

export type TradeFairPage = {
  tradeFairs: TradeFair[]
  page: number
  size: number
  total: number
}

/**
 * Everything that may be set. One shape for create and update, because the backend takes the same
 * fields for both — a PUT that omitted a field could not tell "leave the hall alone" from
 * "clear it".
 */
export type TradeFairInput = {
  name: string
  startDate: string
  endDate: string
  city: string
  hall: string | null
  booth: string | null
  notes: string | null
}

/**
 * The columns the trade fair table has. The state is not among them: it is derived from the period
 * against today and never stored, so there is no column to order by — the start date is that order.
 */
export const TradeFairSort = {
  START_DATE: 'START_DATE',
  NAME: 'NAME',
  CITY: 'CITY',
} as const

export type TradeFairSort = (typeof TradeFairSort)[keyof typeof TradeFairSort]

export type TradeFairListQuery = {
  search?: string
  state?: TradeFairState | null
  sort?: TradeFairSort | null
  direction?: SortDirection | null
  page?: number
  size?: number
}

export const DEFAULT_PAGE_SIZE = 20

export function listTradeFairs(
  query: TradeFairListQuery = {},
  signal?: AbortSignal,
): Promise<TradeFairPage> {
  const parameters = new URLSearchParams()
  if (query.search) {
    parameters.set('search', query.search)
  }
  if (query.state) {
    parameters.set('state', query.state)
  }
  if (query.sort) {
    parameters.set('sort', query.sort)
  }
  if (query.sort && query.direction) {
    parameters.set('direction', query.direction)
  }
  parameters.set('page', String(query.page ?? 0))
  parameters.set('size', String(query.size ?? DEFAULT_PAGE_SIZE))
  return apiFetch<TradeFairPage>(`/api/trade-fairs?${parameters}`, { signal })
}

export function createTradeFair(body: TradeFairInput): Promise<TradeFair> {
  return apiFetch<TradeFair>('/api/trade-fairs', { method: 'POST', body: JSON.stringify(body) })
}

export function updateTradeFair(id: string, body: TradeFairInput): Promise<TradeFair> {
  return apiFetch<TradeFair>(`/api/trade-fairs/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

/**
 * Only possible while nothing references the fair. Once leads are captured at one it is refused —
 * a lead without its fair would have lost the context it was written in.
 */
export function deleteTradeFair(id: string): Promise<void> {
  return apiFetch<void>(`/api/trade-fairs/${encodeURIComponent(id)}`, { method: 'DELETE' })
}
